/**
 * @fileoverview Vendors Page
 * @task B.INV.4 - Vendor Management
 * @theme Polished Corporate - Teal accents, refined shadows
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@components/ui/button';
import { useMarket } from '@contexts/MarketContext';
import { useMarketApi } from '../../hooks/useMarketApi';
import type { Vendor } from '@lib/demo-data';

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
};

/**
 * VendorsPage Component
 * Lists all vendors/suppliers
 */
export function VendorsPage(): JSX.Element {
  const navigate = useNavigate();
  const { formatCurrency, formatTaxId, isBrazil, config, market } = useMarket();
  const { vendorsApi } = useMarketApi();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadVendors = async () => {
      setIsLoading(true);
      try {
        const result = await vendorsApi.getVendors();
        setVendors(result.data);
      } catch (error) {
        console.error('Failed to load vendors:', error);
      } finally {
        setIsLoading(false);
      }
    };
    void loadVendors();
  }, [market, vendorsApi]);

  const totalBalance = vendors.reduce((sum, v) => sum + (v.openBalance ?? 0), 0);
  const activeCount = vendors.filter((v) => v.status === 'active').length;

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="container py-6">
        {/* Action Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm sm:text-base text-gray-500">
            {isBrazil ? 'Gerenciar fornecedores e relacionamentos' : 'Manage your suppliers and vendor relationships'}
          </p>
          <Button
            onClick={() => navigate('/accounts-payable/vendors/new')}
            className="gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm transition-all hover:from-teal-600 hover:to-teal-700 hover:shadow-md min-h-[44px] w-full sm:w-auto"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {isBrazil ? 'Adicionar Fornecedor' : 'Add Vendor'}
          </Button>
        </div>
        {/* Stats Cards */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-xl bg-white p-5 shadow-card transition-all hover:shadow-card-hover">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {isBrazil ? 'Total de Fornecedores' : 'Total Vendors'}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{vendors.length}</p>
              </div>
              <div className="rounded-xl bg-teal-50 p-2.5 text-teal-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-5 shadow-card transition-all hover:shadow-card-hover">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {isBrazil ? 'Fornecedores Ativos' : 'Active Vendors'}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{activeCount}</p>
              </div>
              <div className="rounded-xl bg-emerald-50 p-2.5 text-emerald-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-5 shadow-card transition-all hover:shadow-card-hover">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {isBrazil ? 'Total Pendente' : 'Total Outstanding'}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(totalBalance)}</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-2.5 text-amber-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Vendors Table */}
        <div className="overflow-hidden rounded-xl bg-white shadow-card">
          <div className="border-b border-gray-100 px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                {isBrazil ? 'Todos os Fornecedores' : 'All Vendors'}
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:flex-none">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder={isBrazil ? 'Buscar fornecedores...' : 'Search vendors...'}
                    className="h-10 sm:h-9 w-full sm:w-64 rounded-xl border border-gray-200 bg-gray-50/50 pl-10 pr-4 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-3 sm:px-6 py-2 sm:py-3">{isBrazil ? 'Fornecedor' : 'Vendor'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 hidden md:table-cell">{isBrazil ? config.taxIdLabel : 'Tax ID'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 hidden lg:table-cell">{isBrazil ? 'Contato' : 'Contact'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 hidden lg:table-cell">{isBrazil ? 'Condições de Pagamento' : 'Payment Terms'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3">{isBrazil ? 'Saldo' : 'Balance'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 hidden md:table-cell">{isBrazil ? 'Total Compras' : 'Total'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3">{isBrazil ? 'Status' : 'Status'}</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3">{isBrazil ? 'Ações' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-sm text-gray-500">
                      {isBrazil ? 'Carregando...' : 'Loading...'}
                    </td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-sm text-gray-500">
                      {isBrazil ? 'Nenhum fornecedor encontrado' : 'No vendors found'}
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor) => {
                    // Truncate name for mobile
                    const displayName = vendor.name.length > 20 ? `${vendor.name.substring(0, 20)}...` : vendor.name;
                    return (
                      <tr key={vendor.id} className="transition-colors hover:bg-teal-50/50">
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate max-w-[120px] sm:max-w-none" title={vendor.name}>
                            <span className="sm:hidden">{displayName}</span>
                            <span className="hidden sm:inline">{vendor.name}</span>
                          </p>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                          <p className="text-xs sm:text-sm text-teal-600">
                            {formatTaxId('taxId' in vendor ? vendor.taxId : vendor.id)}
                          </p>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-700 truncate max-w-[150px]">{vendor.email}</p>
                            <p className="text-xs text-gray-400">{vendor.phone}</p>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden lg:table-cell text-xs sm:text-sm text-gray-500">{vendor.paymentTerms}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {(vendor.openBalance ?? 0) > 0 ? formatCurrency(vendor.openBalance ?? 0) : '-'}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell text-xs sm:text-sm text-gray-700 whitespace-nowrap">{formatCurrency(vendor.totalPurchases ?? 0)}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <span
                            className={`inline-flex rounded-full px-2 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-medium capitalize ${statusStyles[vendor.status ?? 'active'] ?? ''}`}
                          >
                            {isBrazil
                              ? (vendor.status ?? 'active') === 'active' ? 'Ativo' : 'Inativo'
                              : (vendor.status ?? 'active')}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <button className="rounded-lg p-2 sm:p-2.5 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] text-gray-400 transition-all hover:bg-teal-50 hover:text-teal-600 flex items-center justify-center">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button className="rounded-lg p-2 sm:p-2.5 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] text-gray-400 transition-all hover:bg-teal-50 hover:text-teal-600 hidden sm:flex items-center justify-center">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <Link
                              to="/accounts-payable/bills/new"
                              className="rounded-lg p-2 sm:p-2.5 min-h-[36px] min-w-[36px] sm:min-h-[40px] sm:min-w-[40px] text-teal-500 transition-all hover:bg-teal-50 hover:text-teal-700 flex items-center justify-center"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
